/**
 * AdminRolesPage — Cargo (role) management in the Admin console.
 * Source-of-truth CRUD for workflow_roles_v9.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Pencil, Check, X } from 'lucide-react';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import {
  getRoles,
  getDepartments,
  createRole,
  updateRole,
  deleteRole,
  type OrgRole,
  type OrgDepartment,
} from '../../../services/organizationService';

interface AdminRolesPageProps {
  externalShowCreate?: boolean;
  onCreateDone?: () => void;
  externalSearch?: string;
  externalDeptFilter?: string;
}

export const AdminRolesPage: React.FC<AdminRolesPageProps> = ({ externalShowCreate, onCreateDone, externalSearch, externalDeptFilter }) => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const qc = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDeptId, setNewDeptId] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDeptId, setEditDeptId] = useState('');

  // React to external trigger
  useEffect(() => {
    if (externalShowCreate) setShowCreate(true);
  }, [externalShowCreate]);
  const rolesQuery = useQuery({
    queryKey: ['admin-roles', orgId],
    enabled: Boolean(orgId),
    queryFn: () => getRoles(orgId),
  });

  const deptsQuery = useQuery({
    queryKey: ['admin-departments-for-roles', orgId],
    enabled: Boolean(orgId),
    queryFn: () => getDepartments(orgId),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin-roles', orgId] });
    qc.invalidateQueries({ queryKey: ['org-structure', orgId] });
  };

  const createMut = useMutation({
    mutationFn: () => createRole(orgId, newName.trim(), newDeptId, undefined),
    onSuccess: () => { invalidateAll(); setNewName(''); setNewDeptId(''); setShowCreate(false); onCreateDone?.(); },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, name, deptId }: { id: string; name: string; deptId: string }) =>
      updateRole(id, { name, departmentId: deptId }),
    onSuccess: () => { invalidateAll(); setEditingId(null); },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: invalidateAll,
  });

  const departments: OrgDepartment[] = deptsQuery.data ?? [];
  const roles: OrgRole[] = rolesQuery.data ?? [];
  const deptName = (id?: string | null) => departments.find(d => d.id === id)?.name ?? '—';

  const startEdit = (r: OrgRole) => {
    setEditingId(r.id);
    setEditName(r.name);
    setEditDeptId(r.department_id ?? '');
  };

  return (
    <RequireCapability capability="roles.manage" realRoleOnly>
        <div className="enterprise-page h-full flex flex-col overflow-hidden">
          {/* Inline create form (shown when triggered from ribbon) */}
          {showCreate && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--border-default)] shrink-0">
                <input
                  className="enterprise-input text-[12px] h-[28px] flex-1 max-w-[200px] bg-white/5 backdrop-blur border-[var(--border-default)]"
                  placeholder="Nome do cargo..."
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && newName.trim() && newDeptId && createMut.mutate()}
                  autoFocus
                />
                <select
                  className="enterprise-input text-[12px] h-[28px] w-[180px] bg-white/5 backdrop-blur border-[var(--border-default)]"
                  value={newDeptId}
                  onChange={e => setNewDeptId(e.target.value)}
                >
                  <option value="">Selecione um departamento...</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="enterprise-btn-primary text-[12px] h-[28px] px-3"
                  disabled={!newName.trim() || !newDeptId || createMut.isPending}
                  onClick={() => createMut.mutate()}
                >
                  {createMut.isPending ? 'Criando...' : 'Criar'}
                </button>
                <button
                  type="button"
                  className="enterprise-btn-ghost text-[12px] h-[28px] px-3"
                  onClick={() => { setShowCreate(false); setNewName(''); setNewDeptId(''); onCreateDone?.(); }}
                >
                  Cancelar
                </button>
            </div>
          )}

          {rolesQuery.isPending && <ModuleLoadingState label="Carregando cargos" />}

          {!rolesQuery.isPending && (
            <>
              {!roles.length ? (
                <p className="text-[12px] enterprise-text-secondary py-4 text-center">
                  Nenhum cargo criado ainda.
                </p>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="enterprise-table-header sticky top-0 z-10">
                      <tr>
                        <th className="enterprise-table-head-cell">Cargo</th>
                        <th className="enterprise-table-head-cell">Departamento</th>
                        <th className="enterprise-table-head-cell">Chave</th>
                        <th className="enterprise-table-head-cell" />
                      </tr>
                    </thead>
                    <tbody>
                      {roles
                        .filter(r => {
                          if (externalSearch) {
                            const s = externalSearch.toLowerCase();
                            if (!r.name?.toLowerCase().includes(s) && !r.role_key?.toLowerCase().includes(s)) return false;
                          }
                          if (externalDeptFilter && externalDeptFilter !== 'all' && r.department_id !== externalDeptFilter) return false;
                          return true;
                        })
                        .map(r => (
                        <tr key={r.id} className="enterprise-table-row">
                          {editingId === r.id ? (
                            <>
                              <td className="enterprise-table-cell">
                                <input
                                  className="enterprise-input text-sm w-full"
                                  value={editName}
                                  onChange={e => setEditName(e.target.value)}
                                  autoFocus
                                />
                              </td>
                              <td className="enterprise-table-cell">
                                <select
                                  className="enterprise-input text-sm"
                                  value={editDeptId}
                                  onChange={e => setEditDeptId(e.target.value)}
                                >
                                  <option value="">Selecione um departamento...</option>
                                  {departments.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="enterprise-table-cell" />
                              <td className="enterprise-table-cell text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    className="p-1 text-green-600 hover:opacity-70"
                                    onClick={() => updateMut.mutate({ id: r.id, name: editName.trim(), deptId: editDeptId })}
                                    disabled={!editName.trim() || !editDeptId || updateMut.isPending}
                                  >
                                    <Check size={14} />
                                  </button>
                                  <button className="p-1 text-neutral-400 hover:opacity-70" onClick={() => setEditingId(null)}>
                                    <X size={14} />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="enterprise-table-cell font-medium">{r.name}</td>
                              <td className="enterprise-table-cell enterprise-text-secondary">{deptName(r.department_id)}</td>
                              <td className="enterprise-table-cell enterprise-text-secondary font-mono text-xs">{r.role_key}</td>
                              <td className="enterprise-table-cell text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-enterprise-text-secondary transition-colors"
                                    onClick={() => startEdit(r)}
                                    title="Editar cargo"
                                  >
                                    <Pencil size={13} />
                                  </button>
                                  <button
                                    className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-enterprise-text-secondary hover:text-red-600 transition-colors"
                                    onClick={() => {
                                      if (confirm('Remover este cargo?')) deleteMut.mutate(r.id);
                                    }}
                                    title="Excluir cargo"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
    </RequireCapability>
  );
};
